import {
  PluginUpdateState as PluginUpdateStateEnum,
  GetPluginUpdateStateRequestSchema,
  RefreshPluginUpdateStateRequestSchema,
  StartPluginUpdateDownloadRequestSchema,
  PluginUpdateStateResponseSchema,
  type PluginUpdateStateResponse,
} from './gen/plugin_update_service_pb';
import type { DescMessage } from '@bufbuild/protobuf';
import { unaryViaDirectGrpcWeb } from './directGrpcWeb';
import { getGrpcBaseUrl } from './transport';
import { getGrpcPort } from './grpcRuntime';
import { flushWebLogger, webLog } from './webLogger';

const SERVICE_PATH = '/pipeex.pluginupdate.v1.PluginUpdateService';

/**
 * Lifecycle of the plugin self-update check, mirrored from the proto enum
 * `pipeex.pluginupdate.v1.PluginUpdateState` (and the C# source of truth,
 * Pipeex.General.PluginUpdate.PluginUpdateState). Kept as a string union so the
 * Zustand store, React components, and tests can compare states without
 * importing the numeric generated enum.
 */
export type PluginUpdateLifecycle =
  | 'UNKNOWN'
  | 'CHECKING'
  | 'UP_TO_DATE'
  | 'UPDATE_AVAILABLE'
  | 'CHECK_FAILED'
  | 'DOWNLOADING'
  | 'VERIFYING'
  | 'READY_TO_APPLY'
  | 'INSTALL_FAILED';

/**
 * UI-facing projection of {@link PluginUpdateStateResponse}. The generated
 * message carries `sizeBytes` as a bigint and the lifecycle as a numeric enum;
 * this snapshot normalises both into shapes the store/components consume
 * directly.
 */
export interface PluginUpdateSnapshot {
  state: PluginUpdateLifecycle;
  installedVersion: string;
  latestVersion: string;
  channel: string;
  sha256: string;
  sizeBytes: number;
  releaseNotesUrl: string;
  downloadUrl: string;
  lastCheckedAtUtc: string;
  errorMessage: string;
  downloadProgressPercent: number;
  stagedVersion: string;
  failureReason: string;
  canRetry: boolean;
  isBelowMinSupported: boolean;
  minSupportedVersion: string;
  autoDownloadEnabled: boolean;
}

function mapLifecycle(state: PluginUpdateStateEnum): PluginUpdateLifecycle {
  switch (state) {
    case PluginUpdateStateEnum.CHECKING:
      return 'CHECKING';
    case PluginUpdateStateEnum.UP_TO_DATE:
      return 'UP_TO_DATE';
    case PluginUpdateStateEnum.UPDATE_AVAILABLE:
      return 'UPDATE_AVAILABLE';
    case PluginUpdateStateEnum.CHECK_FAILED:
      return 'CHECK_FAILED';
    case PluginUpdateStateEnum.DOWNLOADING:
      return 'DOWNLOADING';
    case PluginUpdateStateEnum.VERIFYING:
      return 'VERIFYING';
    case PluginUpdateStateEnum.READY_TO_APPLY:
      return 'READY_TO_APPLY';
    case PluginUpdateStateEnum.INSTALL_FAILED:
      return 'INSTALL_FAILED';
    case PluginUpdateStateEnum.UNKNOWN:
    default:
      return 'UNKNOWN';
  }
}

/**
 * Pure mapper from the generated gRPC response to the UI snapshot. Exported so
 * unit tests can assert the bigint→number and enum→string-union conversions
 * without standing up a transport.
 */
export function mapPluginUpdateState(
  response: PluginUpdateStateResponse
): PluginUpdateSnapshot {
  return {
    state: mapLifecycle(response.state),
    installedVersion: response.installedVersion ?? '',
    latestVersion: response.latestVersion ?? '',
    channel: response.channel ?? '',
    sha256: response.sha256 ?? '',
    sizeBytes: Number(response.sizeBytes ?? 0n),
    releaseNotesUrl: response.releaseNotesUrl ?? '',
    downloadUrl: response.downloadUrl ?? '',
    lastCheckedAtUtc: response.lastCheckedAtUtc ?? '',
    errorMessage: response.errorMessage ?? '',
    downloadProgressPercent: response.downloadProgressPercent ?? 0,
    stagedVersion: response.stagedVersion ?? '',
    failureReason: response.failureReason ?? '',
    canRetry: response.canRetry ?? false,
    isBelowMinSupported: response.isBelowMinSupported ?? false,
    minSupportedVersion: response.minSupportedVersion ?? '',
    autoDownloadEnabled: response.autoDownloadEnabled ?? false,
  };
}

function failedSnapshot(message: string): PluginUpdateSnapshot {
  return {
    state: 'CHECK_FAILED',
    installedVersion: '',
    latestVersion: '',
    channel: '',
    sha256: '',
    sizeBytes: 0,
    releaseNotesUrl: '',
    downloadUrl: '',
    lastCheckedAtUtc: '',
    errorMessage: message,
    downloadProgressPercent: 0,
    stagedVersion: '',
    failureReason: '',
    canRetry: false,
    isBelowMinSupported: false,
    minSupportedVersion: '',
    autoDownloadEnabled: false,
  };
}

/**
 * Shared unary caller over the WebView2-safe direct gRPC-web transport. The
 * embedded plugin server breaks connect-es's streaming reader, so every
 * plugin-update RPC goes through {@link unaryViaDirectGrpcWeb} instead.
 *
 * Returns `null` when no gRPC runtime is available (standalone browser dev),
 * so callers can leave the prompt hidden. A reachable server that errors
 * resolves to a CHECK_FAILED snapshot rather than throwing.
 */
async function callPluginUpdateRpc(
  method: string,
  requestSchema: DescMessage,
  fallbackMessage: string,
  correlationTraceId?: string
): Promise<PluginUpdateSnapshot | null> {
  if (!getGrpcBaseUrl()) {
    // Standalone browser dev mode: no grpcPort, so there is no embedded plugin
    // server to talk to. Leaving the prompt hidden beats a misleading
    // CHECK_FAILED.
    webLog.warn(
      'pluginUpdateClient',
      `${method}: transport unavailable (grpcPort=${getGrpcPort()})`
    );
    return null;
  }

  try {
    const response = await unaryViaDirectGrpcWeb(
      `${SERVICE_PATH}/${method}`,
      requestSchema,
      PluginUpdateStateResponseSchema,
      {},
      correlationTraceId
    );
    await flushWebLogger();
    return mapPluginUpdateState(response as PluginUpdateStateResponse);
  } catch (error) {
    const message = error instanceof Error ? error.message : fallbackMessage;
    webLog.warn('pluginUpdateClient', `${method} failed: ${message}`);
    await flushWebLogger();
    return failedSnapshot(message);
  }
}

/**
 * Read the cached plugin update state from the embedded gRPC server. Does NOT
 * trigger a network manifest check on the plugin side.
 */
export function fetchPluginUpdateState(): Promise<PluginUpdateSnapshot | null> {
  return callPluginUpdateRpc(
    'GetPluginUpdateState',
    GetPluginUpdateStateRequestSchema,
    'Plugin update check failed'
  );
}

/**
 * Force the plugin to re-check the API manifest immediately, then return the
 * refreshed state.
 */
export function refreshPluginUpdateState(correlationTraceId?: string): Promise<PluginUpdateSnapshot | null> {
  return callPluginUpdateRpc(
    'RefreshPluginUpdateState',
    RefreshPluginUpdateStateRequestSchema,
    'Plugin update refresh failed',
    correlationTraceId
  );
}

/**
 * Idempotently trigger the plugin to download and stage the advertised update
 * (Retry button, or manual start when auto-download is off).
 */
export function startPluginUpdateDownload(): Promise<PluginUpdateSnapshot | null> {
  return callPluginUpdateRpc(
    'StartPluginUpdateDownload',
    StartPluginUpdateDownloadRequestSchema,
    'Plugin update download failed'
  );
}
